/* eslint-disable no-nested-ternary */
import {
  BlueBirdTheme, Checkbox, Typography,
} from "@kitbird/core";
import {
  Control, Controller, UseFormSetValue,
} from "react-hook-form";
import {
  CardBody, CardContainer, CardHeader, CardSelectAll, CardTitle,
} from "../BusinessCard";
import {
  LayoutContainer, SelectionGroupBody, SelectionGroupContainer, SelectionGroupHeader,
} from "../../BusinessTrx.styled";
import {
  IBusinessTrxFormValues, SegmentData, SegmentPoolData, SegmentPools,
} from "../../types";
import BaseLoading from "../../../../../components/Loading/BaseLoading";
import EmptyState from "../EmptyState";
import { useFormBusinessTrx } from "../../FormBusinessTrxProvider";

interface StepSegmentSelectionProps {
  control: Control<IBusinessTrxFormValues>;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
}

const StepSegmentSelection = ({ control, setValue }: StepSegmentSelectionProps) => {
  const {
    allSegmentIds,
    isFetchingSegment,
    resSegmentByCompany,
    segments,
    selectedSegmentIds,
    isEditMode,
    selectedCompanyIds,
  } = useFormBusinessTrx();

  return (
    <CardContainer>
      <CardHeader>
        <CardTitle>
          <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
            Pilih segmen
          </Typography>
        </CardTitle>
        {!isEditMode && (
          <CardSelectAll>
            <Controller
              name="pilihSemuaSegmen"
              control={control}
              render={({ field: { value, onChange, ...restField } }) => (
                <Checkbox
                  {...restField}
                  checked={value}
                  onChange={(e) => {
                    const { checked } = e.target;
                    onChange(checked);
                    setValue("selectedSegmentIds", checked ? allSegmentIds : []);
                  }}
                  label="Pilih semua segmen"
                  disabled={isFetchingSegment || allSegmentIds.length === 0}
                  data-testid="select-all-segment-checkbox"
                />
              )}
            />
          </CardSelectAll>
        )}
      </CardHeader>
      <CardBody layout="flex" flexDirection="column">
        {isFetchingSegment ? (
          <BaseLoading />
        ) : resSegmentByCompany.length ? (
          Object.entries(segments)
            .map(([areaName, segmentPools]: [string, SegmentPools]) => (
              Object.entries(segmentPools)
                .map(([poolName, poolData]: [string, SegmentPoolData]) => {
                  const companies = Object.entries(poolData.companies)
                    .filter(([companyCode]) => selectedCompanyIds.includes(`${poolData.pool_id}.${companyCode}`));
                  if (!companies.length) return null;
                  return (
                    <div key={`${areaName}-${poolName}`}>
                      <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                        {`Area ${areaName} > Pool ${poolData.pool_id} - ${poolName}`}
                      </Typography>
                      <LayoutContainer layout="grid" columns="repeat(2, 1fr)" marginTop="12px">
                        {companies.map(([companyCode, segmentList]: [string, SegmentData[]]) => {
                          const allSegmentIdsInCompany = segmentList
                            .filter((s) => s.segment_code)
                            .map((s) => `${poolData.pool_id}.${companyCode}.${s.segment_code}`);
                          return (
                            <SelectionGroupContainer key={companyCode}>
                              <SelectionGroupHeader>
                                <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
                                  {`Perusahaan ${companyCode}`}
                                </Typography>
                              </SelectionGroupHeader>
                              <SelectionGroupBody gridTemplateRows="none">
                                {!isEditMode && (
                                  <Checkbox
                                    label="Pilih semua segmen"
                                    checked={allSegmentIdsInCompany.length > 0
                                      && allSegmentIdsInCompany
                                        .every((id) => selectedSegmentIds.includes(id))}
                                    onChange={(e) => {
                                      const currentSelected = new Set(selectedSegmentIds);
                                      if (e.target.checked) {
                                        allSegmentIdsInCompany
                                          .forEach((id) => currentSelected.add(id));
                                      } else {
                                        allSegmentIdsInCompany
                                          .forEach((id) => currentSelected.delete(id));
                                      }
                                      setValue("selectedSegmentIds", Array.from(currentSelected));
                                    }}
                                  />
                                )}
                                {segmentList.map((segment) => {
                                  if (!segment.segment_code) return null;
                                  const uniqueValue = `${poolData.pool_id}.${companyCode}.${segment.segment_code}`;
                                  return (
                                    <Controller
                                      key={`${areaName}-${poolName}-${companyCode}-${segment.segment_code}`}
                                      name="selectedSegmentIds"
                                      control={control}
                                      defaultValue={[]}
                                      render={({ field: { value, onChange } }) => (
                                        <Checkbox
                                          label={segment.segment_name}
                                          checked={value.includes(uniqueValue)}
                                          onChange={(e) => {
                                            if (isEditMode) {
                                              onChange(e.target.checked ? [uniqueValue] : []);
                                            } else {
                                              const newSelectedIds = new Set(value);
                                              if (e.target.checked) {
                                                newSelectedIds.add(uniqueValue);
                                              } else {
                                                newSelectedIds.delete(uniqueValue);
                                              }
                                              onChange(Array.from(newSelectedIds));
                                            }
                                          }}
                                        />
                                      )}
                                    />
                                  );
                                })}
                              </SelectionGroupBody>
                            </SelectionGroupContainer>
                          );
                        })}
                      </LayoutContainer>
                    </div>
                  );
                })
            ))
        ) : (
          <EmptyState />
        )}
      </CardBody>
    </CardContainer>
  );
};

export default StepSegmentSelection;
